import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, MotionValue, motion, useMotionTemplate, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Logo } from "../Logo";
import { WorkCard } from "./WorkCard";
import { useScrollIdle } from "../../hooks/useScrollIdle";
import { workItems } from "../../data/portfolio";
import { stageHeading } from "../../utils/typography";
import type { CollageWorkItem, WorkItem } from "../../types/work";
type WorkCollageProps = {
  onOpen: (item: WorkItem) => void;
};

/** Vertical head start per column, so the wall still reads as staggered rather than gridded. */
const columnOffsets = ["lg:mt-0", "lg:mt-[7rem]", "lg:mt-[3.5rem]"];
function CollageItem({
  item,
  index,
  progress,
  scrolling,
  onOpen
}: {
  item: CollageWorkItem;
  index: number;
  progress: MotionValue<number>;
  scrolling: boolean;
  onOpen: (item: WorkItem) => void;
}) {
  const reduceMotion = useReducedMotion();
  const drift = reduceMotion ? 0 : item.position.drift;
  const y = useTransform(progress, [0, 1], [drift, -drift]);
  return <motion.div initial={{
    opacity: 0,
    y: 46
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: "-12%"
  }} transition={{
    duration: 0.8,
    delay: Math.min(index * 0.06, 0.3),
    ease: [0.16, 1, 0.3, 1]
  }} className="relative w-full" data-id="element-539" data-mp="11j3ieadfneyo">
      <motion.div style={{
      y
    }} data-id="element-540" data-mp="rr7j4vzm1isv">
        <WorkCard item={item} scrolling={scrolling} onOpen={onOpen} fitMedia data-id="element-541" data-mp="1fimn0t71c868" />
      </motion.div>
    </motion.div>;
}
export function WorkCollage({
  onOpen
}: WorkCollageProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const framesRef = useRef<HTMLDivElement>(null);
  const [framed, setFramed] = useState(false);
  const scrolling = useScrollIdle();
  const reduceMotion = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: stageRef,
    offset: ["start start", "end start"]
  });
  const blurAmount = useTransform(scrollYProgress, [0, 0.08, 0.26], [0, 3, 12]);
  const titleFilter = useMotionTemplate`blur(${blurAmount}px)`;
  const titleOpacity = useTransform(scrollYProgress, [0, 0.1, 0.3], [1, 0.9, 0.42]);
  const titleScale = useTransform(scrollYProgress, [0, 0.4], [1, 1.09]);
  const cueOpacity = useTransform(scrollYProgress, [0, 0.06], [1, 0]);
  const {
    scrollYProgress: driftProgress
  } = useScroll({
    target: stageRef,
    offset: ["start end", "end start"]
  });
  const columns = useMemo(() => [0, 1, 2].map(column => workItems.filter(item => item.position.column === column)), []);

  // Rails track the frame grid, not the whole stage, so they never duplicate the title's own label.
  useEffect(() => {
    const node = framesRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => setFramed(entry.isIntersecting), {
      rootMargin: "-25% 0px -25% 0px"
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <div ref={stageRef} className="relative" data-id="element-542" data-mp="2zftt3jt06v15">
      <AnimatePresence data-id="element-543" data-mp="l7svizw9gx2e">
        {framed && <motion.div aria-hidden="true" className="pointer-events-none fixed inset-x-5 top-8 z-40 hidden items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-white/55 md:inset-x-8 md:flex" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-544" data-mp="l71bmmc10nry">
            <span className="flex items-center gap-4" data-id="element-545" data-mp="29gp3lwqi1ai">004<span className="h-px w-14 bg-white/25" data-id="element-546" data-mp="2arrbhaj70zi" />Selected work</span>

            <span className="flex items-center gap-2.5" data-id="element-547" data-mp="78kyacjnrz0u">
              <Logo className="h-[18px] w-[40px]" data-id="element-548" data-mp="b9mvknu0m106" />
              <span className="text-white/45" data-id="element-549" data-mp="b821t0fiym3d">+</span>
            </span>
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-550" data-mp="g63n7s0faxi0">
        {framed && <motion.div aria-hidden="true" className="pointer-events-none fixed inset-x-8 bottom-28 z-40 hidden items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-white/50 md:flex" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-551" data-mp="1mnie86k4orw5">
            <span className="flex gap-5" data-id="element-552" data-mp="1z6w4hqxgfnby"><span data-id="element-553" data-mp="1z876pmb94n0y">LinkedIn</span><span data-id="element-554" data-mp="1z8pd3brmohml">WhatsApp</span><span data-id="element-555" data-mp="1z51g22mz5d34">Email</span></span>
            <span className="flex items-center gap-2" data-id="element-556" data-mp="1v514cpr9ffzr"><span className="h-1.5 w-1.5 rounded-full bg-[#ff5f56] rec-pulse" data-id="element-557" data-mp="1v3zgtlt25115" />REC</span>
          </motion.div>}
      </AnimatePresence>

      {/* Stage one: the title holds the full frame on its own. */}
      <div className="sticky top-0 z-0 flex h-[100svh] flex-col items-center justify-center overflow-hidden px-5 text-center" data-id="element-558" data-mp="245yzsjnmuf6q">
        <div aria-hidden="true" className="pointer-events-none absolute inset-6 hidden md:block" data-id="element-559" data-mp="2dw1y8zdiwlcg">
          <span className="absolute left-0 top-0 h-7 w-px bg-white/15" data-id="element-560" data-mp="2rvtbuz04nzpx" /><span className="absolute left-0 top-0 h-px w-7 bg-white/15" data-id="element-561" data-mp="2rwcvck5few62" />
          <span className="absolute right-0 top-0 h-7 w-px bg-white/15" data-id="element-562" data-mp="2mxrsecyshydx" /><span className="absolute right-0 top-0 h-px w-7 bg-white/15" data-id="element-563" data-mp="36iihlikz394r" />
          <span className="absolute bottom-0 left-0 h-7 w-px bg-white/15" data-id="element-564" data-mp="36gfim91ue5nw" /><span className="absolute bottom-0 left-0 h-px w-7 bg-white/15" data-id="element-565" data-mp="3bgjypxyu8ydl" />
          <span className="absolute bottom-0 right-0 h-7 w-px bg-white/15" data-id="element-566" data-mp="2xiddbhb2squt" /><span className="absolute bottom-0 right-0 h-px w-7 bg-white/15" data-id="element-567" data-mp="2xk6j6udv771r" />
        </div>

        <motion.div style={reduceMotion ? undefined : {
        filter: titleFilter,
        opacity: titleOpacity,
        scale: titleScale
      }} className="flex flex-col items-center" data-id="element-568" data-mp="31libj7o4i6dn">
          <p className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-[#54f6c6]" data-id="element-569" data-mp="2sk22b391oab0">
            <span className="h-px w-10 bg-[#54f6c6]/50" data-id="element-570" data-mp="2ds06pve0c3gs" />004 / Selected work
          </p>
          <h2 id="work-heading" className={stageHeading} data-id="element-571" data-mp="2iquw5g6hcq9v">
            Selected <span className="text-[#54f6c6]" data-id="element-572" data-mp="24uhg8bhlgwxi">work</span>
          </h2>
          <p className="mx-auto mt-10 max-w-2xl text-[13px] leading-7 text-white/60 sm:text-sm md:text-[15px] md:leading-8 lg:max-w-none lg:whitespace-nowrap" data-id="element-573" data-mp="29srgh0i6enpz">
            A curated collection of motion design, 3D/CGI, and visual effects built for high-end production.
          </p>
        </motion.div>

        <motion.div style={reduceMotion ? undefined : {
        opacity: cueOpacity
      }} className="absolute bottom-14 flex flex-col items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/45" data-id="element-574" data-mp="5lg8djpt0nwx">
          Scroll to reveal the frames
          <span className="h-10 w-px bg-white/25 scroll-hint" data-id="element-575" data-mp="y8su5ro54rp6" />
        </motion.div>
      </div>

      {/* Stage two: frames travel up over the title. */}
      <div ref={framesRef} className="relative z-10 -mt-[14svh] px-5 md:px-8" data-id="element-576" data-mp="p9ficscjnr1a">
        <div className="mx-auto max-w-[1480px]" data-id="element-577" data-mp="kbdy443dtlo8">
          <div className="mb-10 flex items-center justify-between gap-4 border-b border-white/10 pb-4 font-mono text-[11px] uppercase tracking-[0.16em] text-white/40" data-id="element-578" data-mp="kam776kqcc4a">
            <span data-id="element-579" data-mp="3513becofszet">{String(workItems.length).padStart(2, "0")} frames · films + motion design + visual effect reels</span>
            <span className="hidden sm:inline" data-id="element-580" data-mp="1kkvjt8jqtajy">Blur resolves on scroll idle</span>
          </div>

          <div className="relative" data-id="element-581" data-mp="1pj6qxbinl6yh">
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 hidden lg:block" data-id="element-582" data-mp="1pg16waovsv9m">
              {[{
              l: "24%",
              t: "13%"
            }, {
              l: "58%",
              t: "8%"
            }, {
              l: "88%",
              t: "30%"
            }, {
              l: "14%",
              t: "46%"
            }, {
              l: "48%",
              t: "60%"
            }, {
              l: "76%",
              t: "76%"
            }, {
              l: "30%",
              t: "90%"
            }].map(mark => <span key={`${mark.l}-${mark.t}`} className="crosshair absolute h-3 w-3" style={{
              left: mark.l,
              top: mark.t
            }} data-id="element-583" data-mp="287bv2k382txt" />)}
            </div>

            {/* Columns keep every gap identical while the offsets preserve the scattered rhythm. */}
            <div className="relative grid grid-cols-1 items-start gap-12 lg:grid-cols-3 lg:gap-10 xl:gap-14" data-id="element-584" data-mp="iuqhnsdrifgn">
              {columns.map((column, columnIndex) => <div key={columnIndex} className={`flex flex-col gap-12 lg:gap-16 ${columnOffsets[columnIndex]}`} data-id="element-585" data-mp="3zbjxuznbs7h" data-mpi={columnIndex}>
                  {column.map(item => <CollageItem key={item.id} item={item} index={workItems.indexOf(item)} progress={driftProgress} scrolling={scrolling} onOpen={onOpen} data-id="element-586" data-mp="3v536kke7smpj" />)}
                </div>)}
            </div>
          </div>
        </div>
      </div>

    </div>;
}